import { ZAttrMeta } from "src/business/attribute";
import { sortedAttributes } from "src/business/attribute/sortedAttributes";
import { attrsRecordRawToInternal } from "src/business/attribute/values/attrValueRawToInternal";
import {
  ZUnsafeDataMap,
  ZUnsafeValues,
} from "src/business/attribute/ZAttrValue";
import { ZCatalogClassifierItem } from "src/business/catalog/ZCatalog";
import {
  ZCatalogRecord,
  ZRecordClassification,
} from "src/business/records/ZCatalogRecord";
import { ZSpecificationTyped } from "src/business/specification";
import { createSpecDefaultValues } from "src/business/specification/createSpecDefaultValues";
import { getSpecTitle } from "src/business/specification/getSpecTitle";
import { catalogRecordSystemKeys } from "src/common/systemKey";
import {
  ZMfNode,
  ZMfNodeGroup,
  ZMfNodeVertNav,
} from "src/components/MForm/ZMfNode";
import { ZSidLabel } from "src/types";

import { ZClassificationInternal } from "./ZClassificationInternal";

/**
 * Builds a map of class IDs to their parent hierarchies from classification data.
 * @param classification - Array of classification items containing classes with parent relationships
 * @returns A record mapping class IDs to their parent label arrays, or empty object if no data
 */
const buildParentsMap = (
  classification: ZRecordClassification[] | undefined,
): Record<string, ZSidLabel[][]> => {
  if (!classification) {
    return {};
  }

  const parentsMap: Record<string, ZSidLabel[][]> = {};

  classification
    .flatMap((classificationItem) => classificationItem.classes)
    .forEach((cls) => {
      if (cls.parents) {
        parentsMap[cls.id] = cls.parents;
      }
    });

  return parentsMap;
};

export type RecordFormInfo = {
  meta: ZMfNode;
  data: unknown;
};

const createGroup = (
  name: string,
  title: string,
  nodes: ZMfNode[],
): ZMfNodeGroup => ({
  type: "group",
  name,
  title,
  content: {
    node: {
      type: "grid",
      nodes,
    },
  },
});

const attrMeta = (
  attr: Omit<ZAttrMeta, "displayId" | "uid">,
  enabled?: boolean,
): ZMfNode => ({
  type: "attr",
  attr: {
    uid: "",
    displayId: 0,
    ...attr,
  },
  disableIf: enabled ? undefined : { type: "true" },
});

const createPersLink = (
  name: string,
  title: string,
  value?: ZSidLabel | null,
) =>
  attrMeta({
    name,
    title,
    type: { name: "strOption" },
    edit: {
      component: {
        type: "Select",
        options: {
          type: "inline",
          list: value ? [value] : [],
        },
      },
    },
  });

export const makeRecordForm = (
  schemes: ZSpecificationTyped[],
  classifiers: ZCatalogClassifierItem[],
  srcData?: ZCatalogRecord,
  extraIdentifiers?: { nodes: ZMfNode[]; data: Record<string, unknown> },
): RecordFormInfo => {
  const disabledAttrs = new Set(srcData?.readonlyAttributes);
  const parts: ZMfNodeGroup[] = [];
  const data: ZUnsafeDataMap = {};
  if (srcData) {
    const { id, displayId } = srcData;
    parts[0] = createGroup(catalogRecordSystemKeys.first, "Идентификаторы", [
      attrMeta({
        name: "displayId",
        title: "Display Id",
        type: { name: "number", precision: 0 },
        edit: { colSpan: 1 },
      }),
      attrMeta({
        name: "id",
        title: "UUID",
        type: { name: "string" },
        edit: { colSpan: 1 },
      }),
      ...(extraIdentifiers?.nodes ?? []),
    ]);
    data[catalogRecordSystemKeys.first] = {
      id,
      displayId,
      ...extraIdentifiers?.data,
    };
  }
  schemes.forEach((scheme) => {
    const attributes = sortedAttributes(scheme.definition?.attributes);
    parts.push(
      createGroup(
        scheme.name,
        getSpecTitle(scheme),
        attributes.map((attr) => ({
          type: "attr",
          attr,
          disableIf: disabledAttrs.has(attr.uid) ? { type: "true" } : undefined,
        })),
      ),
    );

    const srcDataPart = srcData?.data?.[scheme.name];
    if (srcDataPart) {
      const dataPart: ZUnsafeValues = attrsRecordRawToInternal(
        srcDataPart,
        attributes,
      );
      data[scheme.name] = dataPart;
    }
    if (!srcData) {
      data[scheme.name] = createSpecDefaultValues(scheme);
    }
  });
  // Классификация
  if (classifiers.length > 0) {
    parts.push(
      createGroup(
        catalogRecordSystemKeys.classification,
        "Классификация",
        classifiers.map(({ id, name }) =>
          attrMeta(
            {
              name: id,
              title: name,
              type: {
                name: "array",
                item: {
                  type: { name: "strOption" },
                },
              },
              edit: {
                colSpan: "*",
                component: {
                  type: "ClassesSelect",
                  classifier: { id, label: name },
                  parentsMap: buildParentsMap(srcData?.classification || []),
                },
              },
            },
            true,
          ),
        ),
      ),
    );
    if (srcData) {
      const cls: ZClassificationInternal = {};
      srcData.classification.forEach(({ classifierId, classes }) => {
        cls[classifierId] = classes;
      });
      data[catalogRecordSystemKeys.classification] = cls;
    }
  }
  if (srcData) {
    const { createdBy, createdAt, updatedBy, updatedAt } = srcData;
    const lastPart = createGroup(catalogRecordSystemKeys.last, "Историчность", [
      // TODO: вынести в общую часть SysData
      createPersLink("createdBy", "Автор создания", createdBy),
      attrMeta({
        name: "createdAt",
        title: "Дата создания",
        type: { name: "dateTime" },
      }),
      createPersLink("updatedBy", "Автор последнего изменения", updatedBy),
      attrMeta({
        name: "updatedAt",
        title: "Дата последнего изменения",
        type: { name: "dateTime" },
      }),
    ]);
    parts.push(lastPart);
    data[catalogRecordSystemKeys.last] = {
      createdBy,
      createdAt,
      updatedBy,
      updatedAt,
    };
  }
  const meta: ZMfNodeVertNav = {
    type: "vertNav",
    useNavBar: true,
    nodes: parts,
  };
  if (srcData?.permissions?.canUpdate === false) {
    meta.disableIf = { type: "true" };
  }
  return { meta, data };
};
